// The studio look, shared by every page and kept in localStorage (stored settings format 3).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_SETTINGS, SETTINGS_KEY, readSettings, settingsPins, writeSettings, type StudioSettings } from 'wobbli/formats';
import { detectRegion } from 'wobbli/holidays';
import type { AvatarProps } from 'wobbli/react';
import { SITE_COLOR } from '../config';

/** The site's starting look: the package defaults in Wobbli's colour, and holidays for the visitor's region. */
export const baseSettings = (): StudioSettings => ({ ...DEFAULT_SETTINGS, colorMode: 'custom', color: SITE_COLOR, region: detectRegion() });

function load(): StudioSettings {
  let o: unknown = null;
  try { o = JSON.parse(localStorage.getItem(SETTINGS_KEY) || 'null'); } catch { o = null; }
  return readSettings(o, baseSettings());
}

type Patch = Partial<StudioSettings> | ((s: StudioSettings) => Partial<StudioSettings>);
interface Ctx { s: StudioSettings; set: (p: Patch) => void; replace: (s: StudioSettings) => void }
const SettingsCtx = createContext<Ctx | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [s, setS] = useState<StudioSettings>(load);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(writeSettings(s))); } catch { /* storage unavailable: the page still works */ }
    }, 250);
  }, [s]);
  const set = useCallback((p: Patch) => setS(prev => ({ ...prev, ...(typeof p === 'function' ? p(prev) : p) })), []);
  const replace = useCallback((n: StudioSettings) => setS(n), []);
  const v = useMemo(() => ({ s, set, replace }), [s, set, replace]);
  return <SettingsCtx.Provider value={v}>{children}</SettingsCtx.Provider>;
}

export function useSettings(): Ctx {
  const c = useContext(SettingsCtx);
  if (!c) throw new Error('useSettings outside SettingsProvider');
  return c;
}

/** The avatar props for the studio look (no state, no holiday). */
export function lookProps(s: StudioSettings): AvatarProps {
  const pins = settingsPins(s);
  return {
    name: s.name, shape: pins.shape, traits: pins.traits, color: pins.color, eyeColor: pins.eyeColor, styleFromName: pins.styleFromName,
    hair: pins.hair, hairColor: pins.hairColor, facialHair: pins.facialHair, eyebrows: pins.eyebrows, clothes: pins.clothes, clothesColor: pins.clothesColor, glasses: pins.glasses,
    mouth: s.mouth, hands: s.hands, shadow: s.shadow, shadowColor: s.shadowColor === 'auto' ? undefined : s.shadowColor === 'body' ? 'body' : s.shadowHex,
    shadowStrength: s.shadowK, shadowAnimation: s.shadowAnim, motion: s.motion, speed: s.speed, intensity: s.intensity
  };
}
